import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, Users } from 'lucide-react';

const ProjectCommunication = ({ engagementId, engagement, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Mock messages for demo
    const mockMessages = [
      {
        id: '1',
        sender: 'Admin',
        senderRole: 'admin',
        message: 'Welcome to the project communication board! Use this space to coordinate activities and share updates.',
        timestamp: new Date(Date.now() - 86400000 * 5).toISOString(),
      },
      {
        id: '2',
        sender: engagement?.companyName || 'Corporate Sponsor',
        senderRole: 'corporate',
        message: 'Looking forward to seeing the baseline survey results. Please keep us posted on the timeline.',
        timestamp: new Date(Date.now() - 86400000 * 3).toISOString(),
      },
      {
        id: '3',
        sender: engagement?.agencyName || 'Implementing Agency',
        senderRole: 'ngo',
        message: 'The survey has been completed and we are compiling the results. We will share the report by end of this week.',
        timestamp: new Date(Date.now() - 86400000 * 2).toISOString(),
      },
      {
        id: '4',
        sender: engagement?.companyName || 'Corporate Sponsor',
        senderRole: 'corporate',
        message: 'Great! Also, please confirm if you need any support with the training materials.',
        timestamp: new Date(Date.now() - 86400000).toISOString(),
      }
    ];
    setMessages(mockMessages);
  }, [engagement]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    const message = {
      id: Date.now().toString(),
      sender: currentUser?.contactName || currentUser?.organization || 'You',
      senderRole: currentUser?.role || 'admin',
      message: newMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages([...messages, message]);
    setNewMessage('');
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'hsl(75 25% 45%)',
      corporate: 'hsl(30 10% 25%)',
      ngo: 'hsl(15 60% 55%)'
    };
    return colors[role] || 'hsl(35 20% 50%)';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Admin',
      corporate: 'Sponsor',
      ngo: 'Agency'
    };
    return labels[role] || 'User';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="bg-background rounded-xl border border-border overflow-hidden" style={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="p-4 border-b border-border gradient-primary">
        <div className="flex items-center gap-3 text-white">
          <Users className="w-5 h-5" />
          <div>
            <h3 className="font-semibold">Project Communication</h3>
            <p className="text-sm text-white/80">Real-time coordination between all stakeholders</p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
        {messages.map((msg) => {
          const isOwnMessage = msg.senderRole === currentUser?.role;
          return (
            <div
              key={msg.id}
              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {!isOwnMessage && (
                    <>
                      <span className="text-xs font-semibold" style={{color: getRoleColor(msg.senderRole)}}>
                        {msg.sender}
                      </span>
                      <span 
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                          background: `${getRoleColor(msg.senderRole)}20`,
                          color: getRoleColor(msg.senderRole)
                        }}
                      >
                        {getRoleLabel(msg.senderRole)}
                      </span>
                    </>
                  )}
                  {isOwnMessage && (
                    <span className="text-xs text-gray-500">You</span>
                  )}
                </div>
                <div
                  className={`p-3 rounded-lg ${
                    isOwnMessage
                      ? 'bg-teal-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  <p className="text-sm">{msg.message}</p>
                </div>
                <div className={`text-xs text-gray-500 mt-1 ${isOwnMessage ? 'text-right' : 'text-left'}`}>
                  {formatTimestamp(msg.timestamp)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
        <div className="flex gap-2">
          <button
            type="button"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
            title="Attach file (Coming soon)"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: newMessage.trim() ? '#14B8A6' : '#E5E7EB',
              color: newMessage.trim() ? '#FFFFFF' : '#9CA3AF'
            }}
          >
            <Send className="w-4 h-4" />
            Send
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          💡 Messages are stored for project coordination and can be referenced later
        </p>
      </form>
    </div>
  );
};

export default ProjectCommunication;
